import React, { useState } from "react";
import { Form, Input, Button, Typography, ConfigProvider, App } from "antd";
import { MailOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { sendForgotPasswordEmail } from "../../slices/authSlice";
import LoginImage from "../../assets/login_image.webp";

const { Title, Paragraph, Text } = Typography;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form"); // form | sent
  const [submittedEmail, setSubmittedEmail] = useState("");

  const onFinish = async ({ email }) => {
    setLoading(true);
    try {
      await dispatch(sendForgotPasswordEmail({ email })).unwrap();
      setSubmittedEmail(email);
      setStep("sent");
      message.success({
        content: "Password reset email sent successfully!",
        duration: 3,
      });
    } catch (error) {
      // The backend returns an identical generic success whether or not the
      // email is registered, so there is no "not registered" case to
      // surface here — only genuine send failures (network / 500 / rate limit).
      const errorMsg = typeof error === "string" ? error : error?.message || "";
      message.error({
        content:
          errorMsg || "Failed to send reset instructions. Please try again.",
        duration: 3,
      });
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = () => {
    message.error({
      content: "Please enter a valid email address.",
      duration: 3,
    });
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  const HeaderLogo = () => (
    <div className="absolute top-[1.625rem] left-[1.625rem] text-[#1D2939] text-[1rem] font-normal tracking-[1.2px]">
      Dash
      <span className="text-[1.25rem] font-semibold tracking-normal">ONE</span>
    </div>
  );

  const SentState = () => (
    <div className="w-full flex flex-col gap-[68px]">
      <Title
        level={2}
        className="!m-0 !text-[30px] !font-bold !text-[rgba(0,0,0,0.85)] !leading-[40px]"
      >
        Check your email
      </Title>
      <div className="bg-[#fafafa] border border-[#f5f5f5] rounded-[24px] px-[18px] py-[16px] flex flex-col gap-[10px]">
        <Paragraph className="!m-0 !text-[16px] !leading-[25px] !text-[rgba(0,0,0,0.85)] font-normal">
          We've just sent a password reset email to{" "}
          <Text
            strong
            className="!text-[rgba(0,0,0,0.85)]"
          >
            {submittedEmail}
          </Text>
          .
        </Paragraph>
        <Paragraph className="!m-0 !text-[16px] !leading-[25px] !text-[rgba(0,0,0,0.85)] font-normal">
          Please check your inbox (and your spam/junk folder) for instructions
          on how to reset your password.
        </Paragraph>
        <Paragraph className="!m-0 !text-[14px] !leading-[22px] !text-[rgba(0,0,0,0.45)] font-normal">
          If you don't receive the email within a few minutes, please try
          initiating the password reset process again.
        </Paragraph>
      </div>
      <div className="w-full h-[36px]">
        <Button
          type="primary"
          block
          className="h-full !rounded-[24px] !text-[16px] !font-normal !leading-[24px]"
          onClick={handleBackToLogin}
        >
          Back to Log in
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex justify-center items-center h-[100vh] w-[100vw] relative bg-white">
      <HeaderLogo />
      <div className="flex justify-center items-center flex-1 flex-col gap-[2rem] px-4">
        <div className="w-full max-w-[31.625rem] flex flex-col">
          {step === "sent" ? (
            <SentState />
          ) : (
            <div className="w-full flex flex-col">
              <div className="flex flex-col items-center gap-4 text-center mb-6">
                <div className="w-12 h-12 bg-[#F2F4F7] rounded-xl flex items-center justify-center">
                  <MailOutlined className="text-[#475467] text-[1.5rem]" />
                </div>
                <div className="text-[24px] font-semibold text-[#101828]">
                  Forgot Password?
                </div>
                <div className="text-[14px] font-normal text-[#475467]">
                  No worries, we'll send you reset instructions.
                </div>
              </div>

              <ConfigProvider theme={{ token: { colorPrimary: "#0E49B5" } }}>
                <Form
                  form={form}
                  name="forgot-password"
                  onFinish={onFinish}
                  onFinishFailed={onFinishFailed}
                  autoComplete="off"
                  layout="vertical"
                  className="w-full"
                >
                  <Form.Item
                    label={
                      <span className="text-[14px] font-[500] text-[#344054] font-[Inter]">
                        Email
                      </span>
                    }
                    name="email"
                    rules={[
                      {
                        required: true,
                        message: "Please input your email!",
                      },
                      {
                        type: "email",
                        message: "Please enter a valid email!",
                      },
                    ]}
                    className="mb-4"
                  >
                    <Input
                      placeholder="Enter your email"
                      size="large"
                      className="rounded-lg border-[#D0D5DD] py-3 px-[14px]"
                      style={{
                        fontSize: "16px",
                        color: "black",
                        fontFamily: "Inter",
                      }}
                    />
                  </Form.Item>

                  <Form.Item className="mb-4">
                    <Button
                      type="primary"
                      htmlType="submit"
                      size="large"
                      loading={loading}
                      className="w-full border-none rounded-lg h-12 text-[16px] font-semibold"
                      style={{ fontFamily: "Inter" }}
                    >
                      Reset Password
                    </Button>
                  </Form.Item>

                  <Form.Item className="mb-0">
                    <Button
                      onClick={handleBackToLogin}
                      size="large"
                      className="w-full border-[#D0D5DD] rounded-lg h-12 text-[14px] font-medium text-[#344054]"
                      style={{ fontFamily: "Inter" }}
                    >
                      ← Back to log in
                    </Button>
                  </Form.Item>
                </Form>
              </ConfigProvider>
            </div>
          )}
        </div>
      </div>
      <div className="absolute bottom-[2rem] left-[1.625rem] text-[rgb(71,84,103)] text-[0.75rem] font-[400]">
        @DashONE 2025
      </div>
      <div
        className="flex-1 h-full w-[50vw] self-stretch rounded-[20px_0_0_20px] bg-no-repeat bg-center bg-cover hidden md:block"
        style={{ backgroundImage: `url(${LoginImage})` }}
      />
    </div>
  );
};

export default ForgotPasswordPage;
